/**
 * Supabase Storage Service
 * Handles file uploads to Supabase Storage with organized folder structure
 */

import { supabase } from '../lib/supabase';
import { sanitizeFilename } from '../utils/fileUtils';

export class SupabaseStorageService {
  static bucketName = 'project-files';
  static supabaseUrl = 'https://db.ab-civil.com';
  static maxFileSize = 2 * 1024 * 1024 * 1024; // 2GB
  static supabaseBucketLimit = 2 * 1024 * 1024 * 1024; // 2GB
  // Removed file type restrictions - all file types are now allowed
  static allowedTypes = [];

  /**
   * Validate file before upload
   * @param {File} file - File object to validate
   * @returns {Object} - {isValid: boolean, error?: string}
   */
  static validateFile(file) {
    if (!file) {
      return { isValid: false, error: 'No file provided' };
    }

    // Check against Supabase bucket limit first (more restrictive)
    if (file.size > this.supabaseBucketLimit) {
      const fileSizeMB = Math.round(file.size / (1024 * 1024));
      const limitMB = Math.round(this.supabaseBucketLimit / (1024 * 1024));
      return { 
        isValid: false, 
        error: `File size (${fileSizeMB}MB) exceeds Supabase limit (${limitMB}MB). Please contact admin to increase storage limits or reduce file size.` 
      };
    }

    if (file.size > this.maxFileSize) {
      const maxSizeMB = Math.round(this.maxFileSize / (1024 * 1024));
      return { isValid: false, error: `File size exceeds ${maxSizeMB}MB limit` };
    }

    // All file types are now allowed
    return { isValid: true };
  }

  /**
   * Generate file path with organized folder structure
   * @param {string} projectId - Project ID, Takeoff ID, or Feedback ID
   * @param {string} clientId - Client ID (not required for feedback type)
   * @param {string} originalFilename - Original filename
   * @param {string} type - Type of entity ('project', 'takeoff', 'feedback', or 'export')
   * @returns {string} - Generated file path
   */
  static generateFilePath(projectId, clientId, originalFilename, type = 'project') {
    const timestamp = new Date().toISOString().split('T')[0];
    const uuid = crypto.randomUUID().substring(0, 8);

    // Use centralized filename sanitization utility
    const sanitizedFilename = sanitizeFilename(originalFilename);

    // Get file extension
    const lastDotIndex = sanitizedFilename.lastIndexOf('.');
    const extension = lastDotIndex > 0 ? sanitizedFilename.substring(lastDotIndex) : '';
    const nameWithoutExt = lastDotIndex > 0 ? sanitizedFilename.substring(0, lastDotIndex) : sanitizedFilename;

    // Limit total filename length (Windows has 255 char limit, leave room for path)
    const maxNameLength = 200; // Allows longer filenames while staying under Supabase 1024 char path limit
    const prefixLength = timestamp.length + 1 + uuid.length + 1; // "2025-07-22_002b2c67_"
    const availableLength = maxNameLength - prefixLength - extension.length;

    let finalName = nameWithoutExt;
    if (finalName.length > availableLength) {
      finalName = finalName.substring(0, availableLength - 3) + '...';
    }

    const filename = `${timestamp}_${uuid}_${finalName}${extension}`;

    // Organize by client and project/takeoff/feedback/export
    if (type === 'takeoff') {
      return `client-${clientId}/takeoff-${projectId}/attachments/${filename}`;
    } else if (type === 'feedback') {
      return `feedback/feedback-${projectId}/screenshots/${filename}`;
    } else if (type === 'export') {
      return `client-${clientId}/project-${projectId}/exports/${filename}`;
    } else {
      return `client-${clientId}/project-${projectId}/discrepancies/${filename}`;
    }
  }

  /**
   * Validate that generated URLs are correct public URLs
   * @param {string} url - URL to validate
   * @param {string} filePath - Expected file path
   * @returns {string} - Validated URL
   */
  static validatePublicUrl(url, filePath) {
    const expectedPattern = `${this.supabaseUrl}/storage/v1/object/public/${this.bucketName}/${filePath}`;
    
    if (!url.includes('/storage/v1/object/public/')) {
      console.warn('Generated URL does not match expected public URL pattern:', {
        generated: url,
        expected: expectedPattern,
        issue: 'URL appears to be a signed URL instead of public URL'
      });
    }
    
    console.log('URL validation:', {
      url: url,
      isPublic: url.includes('/storage/v1/object/public/'),
      expectedPattern: expectedPattern
    });
    
    return url;
  }

  /**
   * Upload file to Supabase Storage
   * @param {File} file - File to upload
   * @param {string} projectId - Project ID, Takeoff ID, or Feedback ID
   * @param {string} clientId - Client ID (not required for feedback type)
   * @param {Function} onProgress - Progress callback
   * @param {AbortController} abortController - Optional abort controller for cancellation
   * @param {string} type - Type of entity ('project', 'takeoff', or 'feedback')
   * @returns {Promise<Object>} - File metadata
   */
  static async uploadFile(file, projectId, clientId, onProgress, abortController = null, type = 'project', uploadDate = null) {
    try {
      // Validate file
      const validation = this.validateFile(file);
      if (!validation.isValid) {
        throw new Error(validation.error);
      }

      const filePath = this.generateFilePath(projectId, clientId, file.name, type);
      
      // Upload with progress tracking
      // Note: Supabase doesn't have built-in progress tracking, so we'll simulate it
      if (onProgress) {
        onProgress(10); // Starting upload
      }

      // Create upload options with optional abort signal
      const uploadOptions = {
        cacheControl: '3600',
        upsert: false
      };

      // Add abort signal if controller provided
      if (abortController?.signal) {
        uploadOptions.signal = abortController.signal;
      }

      const { error } = await supabase.storage
        .from(this.bucketName)
        .upload(filePath, file, uploadOptions);

      if (error) {
        console.error('Upload error:', error);
        // Provide more specific error messages
        if (error.message.includes('exceeded the maximum allowed size')) {
          const fileSizeMB = Math.round(file.size / (1024 * 1024));
          throw new Error(`Upload failed: File size (${fileSizeMB}MB) exceeds the bucket limit. Please contact admin to increase storage limits or compress the file.`);
        } else if (error.message.includes('mime type') && error.message.includes('not supported')) {
          throw new Error(`Upload failed: File type "${file.type}" is not supported by the storage bucket configuration. Contact admin to update bucket settings to allow all file types, or convert your file to a supported format.`);
        } else if (error.message.includes('invalid file type')) {
          throw new Error(`Upload failed: File type "${file.type}" is not allowed. Contact admin to configure bucket to accept all file types.`);
        }
        throw new Error(`Upload failed: ${error.message}`);
      }

      if (onProgress) {
        onProgress(90); // Upload complete
      }
      // Generate public URL (bucket is public, no signed URL needed)
      const { data: urlData } = supabase.storage
        .from(this.bucketName)
        .getPublicUrl(filePath);

      // Validate the URL is correct format
      const validatedUrl = this.validatePublicUrl(urlData.publicUrl, filePath);
      if (onProgress) {
        onProgress(100); // All done
      }

      // Return minimal metadata - only what's necessary for the markup_attachment field
      // Use provided uploadDate or default to current date
      const dateToUse = uploadDate ? new Date(uploadDate + 'T00:00:00').toISOString() : new Date().toISOString();

      return {
        id: crypto.randomUUID(),
        filename: file.name,
        url: validatedUrl,
        size: file.size,
        type: file.type,
        uploadDate: dateToUse
      };

    } catch (error) {
      console.error('Error in upload process:', error);
      
      // Handle different error types
      if (error.name === 'AbortError' || (abortController?.signal?.aborted)) {
        throw new Error('Upload cancelled by user');
      } else if (error.message.includes('Failed to fetch')) {
        throw new Error('Unable to connect to storage server. Please check your internet connection.');
      } else if (error.message.includes('storage/bucket-not-found')) {
        throw new Error('Storage bucket not configured. Please contact administrator.');
      } else if (error.message.includes('storage/object-already-exists')) {
        throw new Error('A file with this name already exists. Please rename and try again.');
      }
      
      throw error;
    }
  }

  /**
   * Upload multiple files
   * @param {File[]} files - Array of files to upload
   * @param {string} projectId - Project ID
   * @param {string} clientId - Client ID
   * @param {Function} onProgress - Progress callback for overall progress
   * @returns {Promise<Object[]>} - Array of file metadata
   */
  static async uploadMultipleFiles(files, projectId, clientId, onProgress) {
    const results = [];
    const totalFiles = files.length;
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const completedFiles = i;
      
      try {
        const fileProgress = (progress) => {
          // Calculate overall progress
          const overallProgress = Math.round(
            ((completedFiles * 100) + progress) / totalFiles
          );
          if (onProgress) {
            onProgress(overallProgress);
          }
        };

        const result = await this.uploadFile(file, projectId, clientId, fileProgress);
        results.push(result);
      } catch (error) {
        console.error(`Failed to upload ${file.name}:`, error);
        // Continue with other files even if one fails
        results.push({
          id: crypto.randomUUID(),
          original_filename: file.name,
          error: error.message
        });
      }
    }

    return results;
  }

  /**
   * Generate shareable link for a file
   * @param {string} filePath - File path in storage
   * @returns {Promise<string>} - Shareable URL (public URL since bucket is public)
   */
  static async generateShareLink(filePath) {
    try {
      // For public bucket, just return the public URL
      const { data } = supabase.storage
        .from(this.bucketName)
        .getPublicUrl(filePath);
      return data.publicUrl;
    } catch (error) {
      console.error('Share link generation error:', error);
      throw error;
    }
  }

  /**
   * Delete file from storage
   * @param {string} filePath - File path to delete
   * @returns {Promise<Object>} - Deletion result
   */
  static async deleteFile(filePath) {
    try {
      const { error } = await supabase.storage
        .from(this.bucketName)
        .remove([filePath]);

      if (error) {
        throw new Error(`Failed to delete file: ${error.message}`);
      }

      return { success: true };
    } catch (error) {
      console.error('Delete error:', error);
      throw error;
    }
  }

  /**
   * Get file info from storage
   * @param {string} filePath - File path
   * @returns {Promise<Object|null>} - File metadata or null
   */
  static async getFileInfo(filePath) {
    try {
      const directory = filePath.substring(0, filePath.lastIndexOf('/'));
      const filename = filePath.substring(filePath.lastIndexOf('/') + 1);

      const { data, error } = await supabase.storage
        .from(this.bucketName)
        .list(directory, {
          search: filename
        });

      if (error) {
        throw new Error(`Failed to get file info: ${error.message}`);
      }

      return data && data.length > 0 ? data[0] : null;
    } catch (error) {
      console.error('File info error:', error);
      throw error;
    }
  }

  /**
   * Upload a zip blob to storage and return public URL
   * @param {Blob} zipBlob - The zip file blob
   * @param {string} filename - The filename for the zip
   * @param {string} projectId - Project ID
   * @param {string} clientId - Client ID
   * @returns {Promise<Object>} - Upload result with public URL
   */
  static async uploadZipToStorage(zipBlob, filename, projectId, clientId) {
    try {
      // Create file path for exports - filename already contains date from export_date
      const sanitizedFilename = sanitizeFilename(filename);
      const filePath = `client-${clientId}/project-${projectId}/exports/${sanitizedFilename}`;

      // Upload the zip file (upsert: false since filenames are unique with timestamps)
      const { error } = await supabase.storage
        .from(this.bucketName)
        .upload(filePath, zipBlob, {
          cacheControl: '3600',
          upsert: false,
          contentType: 'application/zip'
        });

      if (error) {
        console.error('Zip upload error:', error);
        throw new Error(`Failed to upload zip: ${error.message}`);
      }
      // Generate public URL
      const { data: urlData } = supabase.storage
        .from(this.bucketName)
        .getPublicUrl(filePath);

      const validatedUrl = this.validatePublicUrl(urlData.publicUrl, filePath);
      return {
        success: true,
        url: validatedUrl,
        filename: sanitizedFilename,
        filePath: filePath,
        size: zipBlob.size
      };

    } catch (error) {
      console.error('Error uploading zip to storage:', error);
      return {
        success: false,
        error: error.message,
        url: null,
        filename: null
      };
    }
  }

  /**
   * Complete file upload process (compatibility method)
   * @param {File} file - File to upload
   * @param {string} projectId - Project ID
   * @param {Function} onProgress - Progress callback
   * @returns {Promise<Object>} - File metadata
   */
  static async uploadFileComplete(file, projectId, onProgress) {
    // For now, we'll use a default client ID or fetch it from project
    // In production, this should be properly obtained from project data
    const clientId = 'default'; // TODO: Get actual client ID from project
    
    return this.uploadFile(file, projectId, clientId, onProgress);
  }

  /**
   * Get file icon based on file type
   * @param {string} fileType - MIME type
   * @returns {string} - Icon name or class
   */
  static getFileIcon(fileType) {
    if (!fileType || typeof fileType !== 'string') {
      return 'file';
    }
    
    if (fileType.startsWith('image/')) {
      return 'image';
    } else if (fileType === 'application/pdf') {
      return 'pdf';
    } else if (fileType.includes('excel') || fileType.includes('spreadsheet')) {
      return 'excel';
    } else if (fileType === 'application/json') {
      return 'json';
    } else if (fileType.includes('text/') || fileType.includes('csv')) {
      return 'text';
    }
    return 'file';
  }

  /**
   * Format file size for display
   * @param {number} bytes - File size in bytes
   * @returns {string} - Formatted size string
   */
  static formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }
}

export default SupabaseStorageService;